// IND-027: «Rotación de materiales» del Panel de planta. Qué materiales
// importan más (clase ABC), cuáles rotan despacio y cuáles están parados.
// Pide sus datos aparte (el resto del Panel no espera) y usa las mismas cifras
// que el detalle del material; el stock y la cobertura salen de la fila de la
// proyección que ya tiene el Panel.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TuneRounded from '@mui/icons-material/TuneRounded';

import {
  getMaterialRotationSummary,
  saveMaterialRotationThresholds,
} from '../services/industrialCostService';
import type {
  MaterialRotationSummary,
  MaterialRotationThresholds,
} from '../services/industrialCostService';
import type { MaterialStockProjectionRow } from '../types/industrialCost';
import { esNumberFormat } from '../utils/numberFormat';
import {
  buildRotationRows,
  rotationDashReason,
  previewCounts,
  ROTATION_GROUPS,
  suggestedValues,
  suggestThresholds,
} from '../utils/materialRotation';
import type { RotationGroup, RotationTableRow } from '../utils/materialRotation';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';
import { realMaterialName, validUnit } from '../utils/plantPanel';
import { DetailError, DetailLoading } from './IndustrialCostMaterialDetailSections';

const MUTED = '#94a3b8';
const RED = '#f87171';
const qty = esNumberFormat({ maximumFractionDigits: 1 });
const whole = esNumberFormat({ maximumFractionDigits: 0 });

// IND-028: sin decimales por encima de 100 (la tabla cabe sin scroll lateral).
function fmt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return Math.abs(value) > 100 ? whole.format(value) : qty.format(value);
}

const realName = realMaterialName;

function dayMonth(value: string): string {
  const [, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}`;
}

function dateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

type Filter = 'all' | 'A' | 'B' | 'C' | 'idle' | 'slow' | 'short' | 'no_unit' | 'other_units';
const SECTION_GROUPS = new Set<string>(['kg', 'embalajes']);

type SortKey = 'material' | 'class' | 'consumed' | 'share' | 'rate' | 'stock' | 'coverage' | 'rotation' | 'trend' | 'last';
const CLASS_RANK: Record<string, number> = { A: 0, B: 1, C: 2 };
const COLUMNS: Array<{ key: SortKey; label: string; align: 'left' | 'right'; first: 'asc' | 'desc'; value: (row: RotationTableRow) => SortValue }> = [
  { key: 'material', label: 'Material', align: 'left', first: 'asc', value: (row) => row.material_code || row.material_name },
  { key: 'class', label: 'Clase', align: 'left', first: 'asc', value: (row) => (row.abc_class ? CLASS_RANK[row.abc_class] : null) },
  { key: 'consumed', label: 'Consumo 12 m', align: 'right', first: 'desc', value: (row) => row.consumed_12m_qty },
  { key: 'share', label: '% planta', align: 'right', first: 'desc', value: (row) => row.share_pct },
  { key: 'rate', label: 'Consumo / día', align: 'right', first: 'desc', value: (row) => row.per_workday },
  { key: 'stock', label: 'Stock hoy', align: 'right', first: 'desc', value: (row) => row.stock },
  { key: 'coverage', label: 'Cobertura (días)', align: 'right', first: 'asc', value: (row) => row.coverage_days },
  { key: 'rotation', label: 'Rotación (días)', align: 'right', first: 'desc', value: (row) => row.rotation_shown },
  { key: 'trend', label: '4 semanas', align: 'right', first: 'desc', value: (row) => row.recent_variation_pct },
  { key: 'last', label: 'Último consumo', align: 'left', first: 'desc', value: (row) => row.last_consumption_date },
];

function trendLabel(value: number | null): { text: string; color: string } {
  if (value === null || !Number.isFinite(value)) return { text: '—', color: MUTED };
  const text = `${whole.format(Math.abs(value))} %`;
  if (value > 0) return { text: `↑ +${text}`, color: '#fbbf24' };
  if (value < 0) return { text: `↓ −${text}`, color: '#38bdf8' };
  return { text: `→ ${text}`, color: MUTED };
}

export default function IndustrialCostMaterialRotationSection({
  stockRows,
  stockReady,
  stockFailed,
  calculationDate,
  onSelect,
}: {
  stockRows: MaterialStockProjectionRow[];
  // Stock y cobertura salen de la proyección del Panel: sin ella no hay cifras.
  stockReady: boolean;
  stockFailed: boolean;
  calculationDate: string | null | undefined;
  onSelect: (row: MaterialStockProjectionRow) => void;
}) {
  const query = useQuery({
    // Con el prefijo de la proyección: las cargas que la invalidan, también.
    queryKey: ['industrial-cost-material-stock-projection', 'rotation'],
    queryFn: getMaterialRotationSummary,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const [group, setGroup] = useState<RotationGroup>('kg');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const sort = useTableSort<SortKey>('consumed', 'desc');

  const summary = query.data;
  const today = (calculationDate ?? summary?.calculation_date ?? '').slice(0, 10);
  const stockByMaterial = useMemo(
    () => new Map(stockRows.map((row) => [row.material_id, row.calculated_stock_qty] as const)),
    [stockRows],
  );
  const projectionById = useMemo(() => new Map(stockRows.map((row) => [row.material_id, row])), [stockRows]);
  const suggestions = useMemo(
    () => (summary ? suggestThresholds({ summary, stockByMaterial }) : null),
    [summary, stockByMaterial],
  );
  // Sin umbrales guardados, los sugeridos; ritmo y cortes A / B ya vienen
  // aplicados por el backend.
  const thresholds = useMemo<MaterialRotationThresholds | null>(
    () =>
      summary && suggestions
        ? summary.thresholds.saved ?? {
            ...suggestedValues(suggestions),
            rhythm_weeks: summary.rhythm_weeks,
            abc_a_pct: summary.abc_a_pct,
            abc_b_pct: summary.abc_b_pct,
          }
        : null,
    [summary, suggestions],
  );
  const rows = useMemo(
    () =>
      summary && thresholds
        ? buildRotationRows({
            rows: summary.rows.filter((row) => row.group === group),
            stockByMaterial,
            today,
            thresholds,
          })
        : [],
    [summary, thresholds, group, stockByMaterial, today],
  );
  // IND-029: ningún material desaparece. Los de otras unidades o sin unidad
  // (fuera de los dos grupos) salen con sus filtros; solo los del Panel (no
  // las cajas genéricas del escandallo, IND-006).
  const extraRows = useMemo(
    () =>
      summary && thresholds
        ? buildRotationRows({
            rows: summary.rows.filter(
              (row) => !SECTION_GROUPS.has(row.group ?? '') && stockByMaterial.has(row.material_id),
            ),
            stockByMaterial,
            today,
            thresholds,
          })
        : [],
    [summary, thresholds, stockByMaterial, today],
  );
  const noUnitRows = [...rows, ...extraRows].filter((row) => row.unit_missing);
  const otherUnitRows = extraRows.filter((row) => !row.unit_missing);

  const counts = {
    A: rows.filter((row) => row.abc_class === 'A').length,
    B: rows.filter((row) => row.abc_class === 'B').length,
    C: rows.filter((row) => row.abc_class === 'C').length,
    idle: rows.filter((row) => row.idle).length,
    slow: rows.filter((row) => row.slow).length,
    short: rows.filter((row) => row.short).length,
  };
  const term = search.trim().toLowerCase();
  // Si el filtro extra se queda sin materiales, vuelve a «Todos».
  const extraEmpty =
    (filter === 'no_unit' && noUnitRows.length === 0) || (filter === 'other_units' && otherUnitRows.length === 0);
  useEffect(() => {
    if (extraEmpty) setFilter('all');
  }, [extraEmpty]);
  const base = filter === 'no_unit' ? noUnitRows : filter === 'other_units' ? otherUnitRows : rows;
  const filtered = base.filter((row) => {
    if (term && !`${row.material_code ?? ''} ${row.material_name ?? ''}`.toLowerCase().includes(term)) return false;
    if (filter === 'A' || filter === 'B' || filter === 'C') return row.abc_class === filter;
    if (filter === 'idle') return row.idle;
    if (filter === 'slow') return row.slow;
    if (filter === 'short') return row.short;
    return true;
  });
  const column = COLUMNS.find((item) => item.key === sort.key) ?? COLUMNS[2];
  const visible = sortRows(filtered, column.value, sort.direction);
  const unitOf = (row: RotationTableRow) =>
    row.group === 'kg' ? 'kg' : row.group === 'embalajes' ? '' : validUnit(row.unit_label) ?? '';

  const cards: Array<{ key: Filter; label: string; value: number; sub: string; color?: string }> = thresholds
    ? [
        { key: 'A', label: 'Clase A', value: counts.A, sub: `${whole.format(thresholds.abc_a_pct)} % del consumo` },
        { key: 'idle', label: 'Parados', value: counts.idle, sub: `con stock y sin consumo en ${thresholds.idle_weeks} semanas`, color: counts.idle > 0 ? RED : undefined },
        { key: 'slow', label: 'Rotación lenta', value: counts.slow, sub: `más de ${thresholds.slow_rotation_days} días de stock medio` },
        { key: 'short', label: `Cobertura < ${thresholds.short_coverage_days} días`, value: counts.short, sub: 'al ritmo habitual', color: counts.short > 0 ? '#fbbf24' : undefined },
      ]
    : [];
  const chips: Array<{ key: Filter; label: string }> = [
    { key: 'all', label: `Todos · ${rows.length}` },
    { key: 'A', label: `Clase A · ${counts.A}` },
    { key: 'B', label: `B · ${counts.B}` },
    { key: 'C', label: `C · ${counts.C}` },
    { key: 'idle', label: `Parados · ${counts.idle}` },
    { key: 'slow', label: `Rotación lenta · ${counts.slow}` },
    ...(noUnitRows.length > 0 ? [{ key: 'no_unit' as Filter, label: `Sin unidad · ${noUnitRows.length}` }] : []),
    ...(otherUnitRows.length > 0 ? [{ key: 'other_units' as Filter, label: `Otras unidades · ${otherUnitRows.length}` }] : []),
  ];

  return (
    <Paper
      sx={{
        bgcolor: '#151f30',
        border: '1px solid rgba(148,163,184,0.15)',
        borderRadius: 3,
        boxShadow: 'none',
        p: 1.75,
        minWidth: 0,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1.25 }}>
        <Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: 20 }}>Rotación de materiales</Typography>
          <Typography sx={{ color: MUTED, fontSize: 13 }}>
            Qué materiales importan más (clase ABC por consumo de 12 meses), cuáles rotan despacio y cuáles están parados.
            Toda la planta, no solo lo que piden las OF.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={group}
            onChange={(_, value: RotationGroup | null) => value && setGroup(value)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1.25, py: 0.35 } }}
          >
            {ROTATION_GROUPS.map((item) => (
              <ToggleButton key={item.key} value={item.key}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="text"
            startIcon={<TuneRounded fontSize="small" />}
            disabled={!summary}
            onClick={() => setThresholdsOpen(true)}
            sx={{ textTransform: 'none', color: MUTED, fontSize: 12 }}
          >
            Ajustar umbrales
          </Button>
        </Stack>
      </Stack>

      {query.isError ? (
        <DetailError error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : stockFailed ? (
        <Alert severity="warning">
          No se pudo recuperar el stock de los materiales: sin él no se pueden calcular la cobertura, los parados ni la
          rotación.
        </Alert>
      ) : !summary || !thresholds || !stockReady ? (
        <DetailLoading height={260} />
      ) : (
        <Stack spacing={1.25}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
            {cards.map((card) => {
              const active = filter === card.key;
              return (
                <Paper
                  key={card.key}
                  variant="outlined"
                  role="button"
                  tabIndex={0}
                  aria-pressed={active}
                  onClick={() => setFilter(active ? 'all' : card.key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') setFilter(active ? 'all' : card.key);
                  }}
                  sx={{
                    p: 1.15,
                    borderRadius: 2,
                    cursor: 'pointer',
                    borderColor: active ? '#38bdf8' : undefined,
                    bgcolor: active ? 'rgba(56,189,248,0.08)' : undefined,
                  }}
                >
                  <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 800 }}>{card.label}</Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 20, fontWeight: 950, color: card.color ?? '#f8fafc' }}>
                    {whole.format(card.value)}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 10.5, mt: 0.2 }}>{card.sub}</Typography>
                </Paper>
              );
            })}
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar por código o nombre"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {chips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  onClick={() => setFilter(chip.key)}
                  color={filter === chip.key ? 'primary' : 'default'}
                  variant={filter === chip.key ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 800 }}
                />
              ))}
              {filter === 'short' ? (
                <Chip label={`Cobertura < ${thresholds.short_coverage_days} días · ${counts.short}`} size="small" color="primary" sx={{ fontWeight: 800 }} />
              ) : null}
            </Stack>
          </Stack>

          {/* IND-028: alto de la ventana menos la cabecera de la sección
              (excepción a las 10 filas) y sin scroll lateral desde 1280 px. */}
          <TableContainer sx={{ maxHeight: 'max(360px, calc(100vh - 330px))' }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 900,
                '& .MuiTableCell-root': { px: 0.75, py: 0.5, fontSize: 12 },
                '& .MuiTableCell-head': { fontSize: 11, lineHeight: 1.15, verticalAlign: 'bottom' },
              }}
            >
              <TableHead>
                <TableRow>
                  {COLUMNS.map((item) => (
                    <SortableHeaderCell
                      key={item.key}
                      label={item.label}
                      columnKey={item.key}
                      sort={sort}
                      align={item.align}
                      firstDirection={item.first}
                      sx={{ bgcolor: '#151f30', whiteSpace: 'normal' }}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((row) => {
                  const projection = projectionById.get(row.material_id);
                  const trend = trendLabel(row.recent_variation_pct);
                  return (
                    <TableRow
                      key={row.material_id}
                      hover={Boolean(projection)}
                      onClick={projection ? () => onSelect(projection) : undefined}
                      sx={{ cursor: projection ? 'pointer' : undefined }}
                    >
                      <TableCell sx={{ minWidth: 150, maxWidth: 230 }}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: 12 }}>
                          {row.material_code || realName(row.material_name, null) || '—'}
                        </Typography>
                        {row.material_code && realName(row.material_name, row.material_code) ? (
                          <Typography sx={{ color: MUTED, fontSize: 10.5, lineHeight: 1.2 }}>
                            {realName(row.material_name, row.material_code)}
                          </Typography>
                        ) : null}
                        {/* IND-029: materia prima sin unidad (cuenta como kg). */}
                        {row.unit_missing ? (
                          <Typography component="span" sx={{ color: '#fbbf24', fontSize: 10, fontWeight: 800 }}>
                            sin unidad
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>
                        {SECTION_GROUPS.has(row.group ?? '') ? row.abc_class ?? '—' : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{`${fmt(row.consumed_12m_qty)} ${unitOf(row)}`.trim()}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.share_pct === null || !SECTION_GROUPS.has(row.group ?? '')
                          ? '—'
                          : `${esNumberFormat({ maximumFractionDigits: 1 }).format(row.share_pct)} %`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(row.per_workday)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: row.stock !== null && row.stock < 0 ? RED : undefined }}>
                        {fmt(row.stock)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: row.short ? '#fbbf24' : undefined }}>
                        {row.no_consumption ? 'sin consumo' : fmt(row.coverage_days)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: row.slow ? 900 : undefined }}>
                        {/* IND-028: periodo menor de 3 meses: «desde dd/mm» al pasar el ratón. */}
                        {row.rotation_shown !== null && row.rotation_from ? (
                          <MuiTooltip title={`desde ${dayMonth(row.rotation_from)}`}>
                            <span>{fmt(row.rotation_shown)}*</span>
                          </MuiTooltip>
                        ) : row.rotation_shown === null ? (
                          // El «—» siempre con su motivo al pasar el ratón.
                          <MuiTooltip title={`— · ${rotationDashReason(row)}`}>
                            <span style={{ cursor: 'help' }}>—</span>
                          </MuiTooltip>
                        ) : (
                          fmt(row.rotation_shown)
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ color: trend.color, whiteSpace: 'nowrap' }}>{trend.text}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: row.idle ? RED : undefined, fontWeight: row.idle ? 800 : undefined }}>
                        {row.idle
                          ? row.last_consumption_date
                            ? `parado desde ${dayMonth(row.last_consumption_date)}`
                            : 'parado (sin consumo)'
                          : row.last_consumption_date
                            ? dayMonth(row.last_consumption_date)
                            : 'sin consumo'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} sx={{ color: MUTED }}>
                      Ningún material con este filtro.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ color: MUTED, fontSize: 10.5 }}>
            {`Rotación de los últimos 3 meses (* desde el primer recuento válido si es más corto, mínimo 4 semanas${summary.entries_complete?.date ? `; entradas completas desde ${dayMonth(summary.entries_complete.date)}` : ''}) y cobertura al ritmo habitual de ${summary.rhythm_weeks} semanas, como en el detalle de cada material.`}
            {summary.thresholds.saved ? '' : ' Umbrales: los valores por defecto (sin guardar).'}
          </Typography>
        </Stack>
      )}

      {summary && thresholds && suggestions ? (
        <RotationThresholdsDialog
          open={thresholdsOpen}
          onClose={() => setThresholdsOpen(false)}
          summary={summary}
          current={thresholds}
          suggested={suggestions}
          groupRows={summary.rows.filter((row) => row.group === group)}
          groupLabel={ROTATION_GROUPS.find((item) => item.key === group)?.label ?? ''}
          stockByMaterial={stockByMaterial}
          today={today}
        />
      ) : null}
    </Paper>
  );
}

type FieldKey = keyof MaterialRotationThresholds;
const FIELDS: Array<{
  key: FieldKey;
  label: string;
  unit: string;
  meaning: string;
  min: number;
  max: number;
  preview: (count: number) => string;
}> = [
  { key: 'idle_weeks', label: 'Material parado', unit: 'semanas', meaning: 'Con stock y sin ningún consumo en estas semanas.', min: 1, max: 52, preview: (n) => `${n} parados` },
  { key: 'slow_rotation_days', label: 'Rotación lenta', unit: 'días', meaning: 'Más de estos días de stock medio (últimos 3 meses o desde el primer recuento).', min: 1, max: 365, preview: (n) => `${n} con rotación lenta` },
  { key: 'short_coverage_days', label: 'Cobertura corta', unit: 'días', meaning: 'El stock de hoy cubre menos de estos días laborables al ritmo habitual.', min: 1, max: 120, preview: (n) => `${n} con cobertura corta` },
  { key: 'rhythm_weeks', label: 'Ritmo habitual', unit: 'semanas', meaning: 'Semanas que se promedian para el consumo por día laborable (también en el detalle).', min: 2, max: 26, preview: (n) => `con este ritmo, ${n} con cobertura corta` },
  { key: 'abc_a_pct', label: 'Corte de la clase A', unit: '% acumulado', meaning: 'Los materiales que suman hasta este % del consumo de su grupo son A.', min: 50, max: 98, preview: (n) => `${n} de clase A` },
  { key: 'abc_b_pct', label: 'Corte de la clase B', unit: '% acumulado', meaning: 'Hasta este % son B; el resto, C.', min: 51, max: 99, preview: (n) => `${n} de clase B` },
];

function RotationThresholdsDialog({
  open,
  onClose,
  summary,
  current,
  suggested,
  groupRows,
  groupLabel,
  stockByMaterial,
  today,
}: {
  open: boolean;
  onClose: () => void;
  summary: MaterialRotationSummary;
  current: MaterialRotationThresholds;
  suggested: ReturnType<typeof suggestThresholds>;
  groupRows: MaterialRotationSummary['rows'];
  groupLabel: string;
  stockByMaterial: ReadonlyMap<string, number | null>;
  today: string;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<FieldKey, string>>(() => toDraft(current));
  const entriesComplete = summary.entries_complete;
  const [entriesDate, setEntriesDate] = useState(entriesComplete?.date?.slice(0, 10) ?? '');
  // Al abrir, los valores en uso (solo al abrir: no pisa lo que se escribe).
  useEffect(() => {
    if (open) {
      setDraft(toDraft(current));
      setEntriesDate(entriesComplete?.date?.slice(0, 10) ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parsed = Object.fromEntries(FIELDS.map((field) => [field.key, Number(draft[field.key].replace(',', '.'))])) as MaterialRotationThresholds;
  const errors = Object.fromEntries(
    FIELDS.map((field) => {
      const value = parsed[field.key];
      const integer = field.key !== 'abc_a_pct' && field.key !== 'abc_b_pct';
      const invalid =
        draft[field.key].trim() === '' ||
        !Number.isFinite(value) ||
        value < field.min ||
        value > field.max ||
        (integer && !Number.isInteger(value));
      return [field.key, invalid ? `Entre ${field.min} y ${field.max}${integer ? ', sin decimales' : ''}` : null];
    }),
  ) as Record<FieldKey, string | null>;
  if (!errors.abc_a_pct && !errors.abc_b_pct && parsed.abc_a_pct >= parsed.abc_b_pct) {
    errors.abc_b_pct = 'Tiene que ser mayor que el corte de la A';
  }
  // Una fecha guardada no se puede vaciar, ni poner en el futuro.
  const entriesDateInvalid =
    (entriesComplete?.source === 'saved' && !entriesDate) || (Boolean(entriesDate) && entriesDate > today);
  const valid = FIELDS.every((field) => !errors[field.key]) && !entriesDateInvalid;
  const preview = valid ? previewCounts({ summary, groupRows, stockByMaterial, today, values: parsed }) : null;

  const mutation = useMutation({
    mutationFn: saveMaterialRotationThresholds,
    onSuccess: () => {
      // Ritmo y cortes cambian también el detalle de cada material.
      void queryClient.invalidateQueries({ queryKey: ['industrial-cost-material-stock-projection', 'rotation'] });
      void queryClient.invalidateQueries({ queryKey: ['industrial-cost-material-stock-projection', 'detail'] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>Ajustar umbrales</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ color: MUTED, fontSize: 12, mb: 1.5 }}>
          {`Se aplican a todos los usuarios de la empresa al guardar. La vista previa cuenta los materiales de ${groupLabel.toLowerCase()}.`}
        </Typography>
        <Stack spacing={1.25}>
          {FIELDS.map((field) => {
            const suggestion = suggested[field.key];
            return (
              <Box
                key={field.key}
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 1.4fr' }, gap: 1, alignItems: 'center' }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 850, fontSize: 13 }}>{field.label}</Typography>
                  <Typography sx={{ color: MUTED, fontSize: 11 }}>{field.meaning}</Typography>
                  <Typography sx={{ color: MUTED, fontSize: 11 }}>{`Ahora: ${whole.format(current[field.key])} ${field.unit}`}</Typography>
                </Box>
                <TextField
                  size="small"
                  value={draft[field.key]}
                  onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
                  error={Boolean(errors[field.key])}
                  helperText={errors[field.key] ?? (preview ? field.preview(preview[field.key]) : ' ')}
                  InputProps={{ endAdornment: <Typography sx={{ color: MUTED, fontSize: 11, ml: 0.5, whiteSpace: 'nowrap' }}>{field.unit}</Typography> }}
                />
                <Box>
                  <Typography sx={{ fontSize: 12 }}>
                    {`Sugerido: ${whole.format(suggestion.value)} ${field.unit}`}
                  </Typography>
                  <Typography sx={{ color: MUTED, fontSize: 11 }}>{suggestion.reason}</Typography>
                  <Button
                    size="small"
                    onClick={() => setDraft({ ...draft, [field.key]: String(suggestion.value) })}
                    sx={{ textTransform: 'none', px: 0, minWidth: 0 }}
                  >
                    Usar sugerido
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Stack>
        {/* IND-031: fecha guardada por empresa, editable, con su porqué. */}
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 1.4fr' }, gap: 1, alignItems: 'center', mt: 1.25 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 850, fontSize: 13 }}>Entradas completas desde</Typography>
            <Typography sx={{ color: MUTED, fontSize: 11 }}>
              Desde esta fecha PROA tiene todos los albaranes. La rotación empieza en el primer recuento igual o posterior.
            </Typography>
          </Box>
          <TextField
            size="small"
            type="date"
            value={entriesDate}
            onChange={(event) => setEntriesDate(event.target.value)}
            inputProps={{ max: today }}
            error={entriesDateInvalid}
            helperText={
              entriesDateInvalid
                ? 'Pon una fecha que no sea futura'
                : entriesDate && entriesDate !== entriesComplete?.date?.slice(0, 10)
                  ? 'Cambiar esta fecha recalcula la rotación de todos los materiales'
                  : ' '
            }
          />
          <Typography sx={{ color: MUTED, fontSize: 11 }}>
            {entriesComplete?.source === 'saved'
              ? `Fecha guardada${entriesComplete.updated_at ? ` · ${entriesComplete.updated_by_label ?? 'usuario desconocido'} · ${dateTime(entriesComplete.updated_at)}` : ''}. Lo anterior no se usa porque los albaranes no están completos.`
              : !entriesComplete?.date
                ? 'Sin fecha de entradas completas todavía.'
                : entriesComplete.source === 'daily_load'
                ? `Calculada: la más antigua de los albaranes nuevos de la primera carga diaria (${dateTime(entriesComplete.run_at)}).${entriesComplete.partial ? ' Esa carga traía albaranes que ya estaban en PROA: la fecha real puede ser anterior.' : ''}`
                : 'Calculada: no se pudo fechar la primera carga diaria y se usa el primer albarán cargado.'}
          </Typography>
        </Box>
        <Typography sx={{ color: MUTED, fontSize: 11, mt: 1.5 }}>
          {summary.thresholds.saved
            ? `Último cambio: ${summary.thresholds.updated_by_label ?? 'usuario desconocido'} · ${dateTime(summary.thresholds.updated_at)}`
            : 'Todavía no se han guardado umbrales: se usan los valores por defecto (los datos, como referencia).'}
        </Typography>
        {mutation.isError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            No se pudieron guardar los umbrales. Vuelve a intentarlo.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => setDraft(toDraft(suggestedValues(suggested)))} sx={{ textTransform: 'none' }}>
          Volver a los sugeridos
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!valid || mutation.isPending}
            // Solo si se ha cambiado: una fecha calculada no se fija al guardar.
            onClick={() =>
              mutation.mutate({
                ...parsed,
                entries_complete_from:
                  entriesDate && entriesDate !== entriesComplete?.date?.slice(0, 10) ? entriesDate : null,
              })
            }
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Guardar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function toDraft(values: MaterialRotationThresholds): Record<FieldKey, string> {
  return Object.fromEntries(FIELDS.map((field) => [field.key, String(values[field.key])])) as Record<FieldKey, string>;
}
